/**
 * useServiceConfig — shared hook for the tenant's /api/service/config doc.
 *
 * Returns:
 *   - config:     raw config from the server (null while loading)
 *   - visibility: full, defaults-merged field_visibility_config tree (never null)
 *   - loading:    bool
 *   - reload():   refetches from the server
 *
 * The `visibility` tree is ALWAYS fully populated — callers can safely read
 * e.g. `visibility.job_cards.columns.party_name` without nullish checks.
 *
 * The hook also listens for a `service-config:changed` CustomEvent so that
 * Service Settings can broadcast a fresh save and every consumer re-syncs
 * within the same tab. (Cross-tab sync is not wired — config changes are
 * admin-only and rare.)
 */
import React from 'react';
import axios from 'axios';
import { DEFAULT_FIELD_VISIBILITY, deepMergeVisibility } from '@/hooks/serviceVisibilityDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useServiceConfig() {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchCfg = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service/config`);
      setConfig(data || {});
    } catch {
      setConfig({});
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchCfg(); }, [fetchCfg]);

  // Same-tab live refresh when ServiceSettings saves
  React.useEffect(() => {
    const onChange = () => fetchCfg();
    window.addEventListener('service-config:changed', onChange);
    return () => window.removeEventListener('service-config:changed', onChange);
  }, [fetchCfg]);

  const visibility = React.useMemo(() => {
    return deepMergeVisibility(DEFAULT_FIELD_VISIBILITY, config?.field_visibility_config);
  }, [config]);

  return { config, visibility, loading, reload: fetchCfg };
}

/** Fire-and-forget helper used by ServiceSettings after a successful save. */
export function broadcastServiceConfigChanged() {
  try { window.dispatchEvent(new CustomEvent('service-config:changed')); } catch { /* noop */ }
}

export default useServiceConfig;
